import { useMemo } from 'react';
import { type UserAccountExclusiveListing, type Listing } from '@pkgs/api/models';

import { Placeholder } from '@/icons/small/Placeholder';
import { useSettings } from '@/hooks/useSettings';
import { useKeyInfo } from '@/hooks/useKeyInfo';
import { useProjectUnits } from '@/hooks/queries/useProjectUnits';
import { useListing } from '@/hooks/queries/useListing';

import { getListingKeyInfoData } from './KeyInfo.data';
import { IconList } from '../IconList';

import styles from './KeyInfo.module.css';

const ListingKeyInfo = ({ listing }: { listing: Listing | UserAccountExclusiveListing }) => {
  const { language } = useSettings();
  const { data: projectUnitsResponse } = useProjectUnits();

  const keyInfoItems = useKeyInfo({
    listing: listing,
    items: getListingKeyInfoData(listing, projectUnitsResponse?.listingsTotal ?? 0, language),
  });

  const filteredKeyInfoItems = useMemo(() => keyInfoItems.filter((i) => i.formattedWithLabel), [keyInfoItems]);

  const itemsWithFallback = useMemo(
    () =>
      filteredKeyInfoItems.map(({ formattedWithLabel, icon, id }) => ({
        id,
        label: formattedWithLabel ?? '',
        icon: icon || <Placeholder />,
        dataTestId: `key-info-${id}`,
      })),
    [filteredKeyInfoItems],
  );

  if (!filteredKeyInfoItems.length) return null;

  return <IconList className={styles.iconList} items={itemsWithFallback} data-testid="expose-key-info" />;
};

const RestrictedKeyInfo = () => {
  const fakeKeyInfoItems = Array.from({ length: 4 })
    .fill(0)
    .map((_, index) => ({
      id: `fake-key-info-${index}`,
      label: `Fake Key Info ${index}`,
      icon: <Placeholder />,
    }));

  return <IconList className={styles.iconList} data-restricted items={fakeKeyInfoItems} data-testid="expose-key-info" />;
};

export const KeyInfo = () => {
  const {
    data: { listing, userAccountExclusiveListing },
  } = useListing();

  return listing || userAccountExclusiveListing ? (
    <ListingKeyInfo listing={listing ?? userAccountExclusiveListing} />
  ) : (
    <RestrictedKeyInfo />
  );
};
